import { forwardRef, useImperativeHandle, useRef } from 'react';

import { VStack } from '../VStack';
import { joinCn } from '../util';
import VTreeNode from './VTreeNode';
import { useVTreeContext } from './hook';
import type { VTreeRef } from './type';
import { getVTreeNodeList } from './util';

const VInnerTree = (
  { className, ...props }: React.ComponentPropsWithRef<'div'>,
  ref?: React.ForwardedRef<VTreeRef>,
) => {
  const {
    nodeById,
    nodeList,
    closeNode,
    deselectAllNode,
    deselectNode,
    emit,
    openNode,
    selectNode,
    setChildren,
  } = useVTreeContext();
  const elRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () =>
      ({
        get el() {
          return elRef.current;
        },
        nodeById,
        nodeList,
        closeNode(value) {
          const idList = closeNode(value);

          emit('close', { result: getVTreeNodeList(nodeById)(idList) });

          return idList;
        },
        deselectAllNode,
        deselectNode,
        openNode(value) {
          const idList = openNode(value);

          emit('open', { result: getVTreeNodeList(nodeById)(idList) });

          return idList;
        },
        selectAllNode() {
          return this.selectNode(nodeList);
        },
        selectNode(value) {
          const idList = selectNode(value);

          idList &&
            emit('select', { result: getVTreeNodeList(nodeById)(idList) });

          return idList;
        },
        setChildren,
      }) as VTreeRef,
    [
      nodeById,
      nodeList,
      deselectAllNode,
      deselectNode,
      closeNode,
      emit,
      openNode,
      selectNode,
      setChildren,
    ],
  );

  return (
    <VStack
      ref={elRef}
      role="tree"
      className={joinCn(className, 'px-1 py-2')}
      {...props}
      data-testid="v-inner-tree"
    >
      {nodeList.map(node => (
        <VTreeNode key={node.id} {...node} />
      ))}
    </VStack>
  );
};

export default forwardRef(VInnerTree) as (
  props: React.ComponentPropsWithRef<'div'> & {
    ref?: React.ForwardedRef<VTreeRef>;
  },
) => ReturnType<typeof VInnerTree>;
